// services/questionService.js
import { questionsAPI } from './api';

export const questionService = {
  async getSurveyQuestions(categoryId) {
    try {
      console.log('🔄 [QUESTION SERVICE] Fetching questions for category:', categoryId);
      console.log('📡 [QUESTION SERVICE] Making API call to /SurveyQuestions/category/' + categoryId);
      
      // Log authentication status
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      console.log('🔐 [QUESTION SERVICE] Auth token exists:', !!token);
      
      const response = await questionsAPI.getByCategory(categoryId);
      
      console.log('✅ [QUESTION SERVICE] API Response received:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        dataType: typeof response.data,
        isArray: Array.isArray(response.data),
        dataLength: Array.isArray(response.data) ? response.data.length : 'N/A'
      });
      
      // Handle the new response format from SurveyQuestionsController
      let questions = [];
      
      if (response.data && response.data.success) {
        // New format: { success: true, message: string, data: { questions: [] } }
        console.log('📊 [QUESTION SERVICE] Response follows new API format');
        
        if (response.data.data && Array.isArray(response.data.data.questions)) {
          console.log('📊 [QUESTION SERVICE] Questions found in data.data.questions');
          questions = response.data.data.questions;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          console.log('📊 [QUESTION SERVICE] Questions found in data.data array');
          // If the entire data is the questions array
          questions = response.data.data;
        } else {
          console.log('📊 [QUESTION SERVICE] No questions array found in response data');
          questions = [];
        }
      } else if (Array.isArray(response.data)) {
        console.log('📊 [QUESTION SERVICE] Response is direct array (legacy format)');
        questions = response.data;
      } else if (response.data && Array.isArray(response.data.questions)) {
        console.log('📊 [QUESTION SERVICE] Response has questions array in data.questions');
        questions = response.data.questions;
      } else if (response.data && Array.isArray(response.data.items)) {
        console.log('📊 [QUESTION SERVICE] Response has questions array in data.items');
        questions = response.data.items;
      } else if (response.data) {
        console.log('📊 [QUESTION SERVICE] Response is single object, wrapping in array');
        questions = [response.data];
      } else {
        console.log('📊 [QUESTION SERVICE] Response data is null/undefined, returning empty array');
        questions = [];
      }
      
      console.log('🎯 [QUESTION SERVICE] Final processed questions:', {
        count: questions.length,
        questions: questions
      });
      
      return questions;
    } catch (error) {
      console.error('❌ [QUESTION SERVICE] Error in getSurveyQuestions:', error);
      
      console.error('🔍 [QUESTION SERVICE] Detailed error info:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        },
        response: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers
        }
      });
      
      // If it's a 404, return empty array (no questions yet)
      if (error.response?.status === 404) {
        console.log(`📝 [QUESTION SERVICE] No questions found for category ${categoryId} (404)`);
        return [];
      }
      
      // If it's a 401/403, throw authentication error
      if (error.response?.status === 401 || error.response?.status === 403) {
        throw new Error('Authentication failed. Please log in again.');
      }
      
      throw new Error(error.response?.data?.message || 'Failed to load survey questions');
    }
  },

  async getSurveyWithCategories(surveyId) {
    try {
      console.log('🔄 Fetching survey with categories and questions:', surveyId);
      
      const response = await questionsAPI.getSurveyWithCategories(surveyId);
      
      console.log('✅ Survey with categories response:', {
        data: response.data,
        categoriesCount: response.data?.data?.categories?.length || 0
      });
      
      // Handle new API response format
      if (response.data && response.data.success) {
        return response.data.data;
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching survey with categories:', error);
      
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        surveyId: surveyId
      });
      
      throw new Error(error.response?.data?.message || 'Failed to load survey with categories');
    }
  },

  async getSurveyQuestion(id) {
    try {
      const response = await questionsAPI.getById(id);
      
      // Handle new API response format
      if (response.data && response.data.success) {
        return response.data.data;
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching survey question:', error);
      throw new Error(error.response?.data?.message || 'Failed to load survey question');
    }
  },

  async createSurveyQuestion(questionData) {
    try {
      console.log('📤 Creating survey question:', questionData);
      
      // Transform data to match backend expectations
      const payload = {
        questionText: questionData.questionText,
        type: questionData.type,
        categoryId: questionData.categoryId,
        order: questionData.order,
        isRequired: questionData.isRequired,
        placeholder: questionData.placeholder,
        helpText: questionData.helpText,
        optionsJson: questionData.optionsJson ?? (
          Array.isArray(questionData.options)
            ? JSON.stringify(questionData.options.map(option => typeof option === 'string' ? option : option.label))
            : null
        ),
        settingsJson: questionData.settingsJson ?? null,
        validationRulesJson: questionData.validationRulesJson ?? null,
        styleJson: questionData.styleJson ?? null,
        minValue: questionData.minValue,
        maxValue: questionData.maxValue,
        defaultValue: questionData.defaultValue
      };
      
      console.log('📦 Sending payload:', payload);
      
      const response = await questionsAPI.create(payload);
      
      console.log('✅ Survey question created response:', response.data);
      
      // Handle new API response format
      if (response.data && response.data.success) {
        return response.data.data;
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Error creating survey question:', error);
      const errorMessage = error.response?.data?.title || 
                          error.response?.data?.message || 
                          (error.response?.data?.errors ? JSON.stringify(error.response.data.errors) : 'Unknown error') ||
                          'Failed to create survey question';
      throw new Error(errorMessage);
    }
  },

  async updateSurveyQuestion(id, questionData) {
    try {
      console.log(`📤 Updating survey question ${id}:`, questionData);
      
      // Transform data to match backend expectations
      const payload = {
        questionText: questionData.questionText,
        type: questionData.type,
        categoryId: questionData.categoryId,
        order: questionData.order,
        isRequired: questionData.isRequired,
        placeholder: questionData.placeholder,
        helpText: questionData.helpText,
        optionsJson: questionData.optionsJson ?? (
          Array.isArray(questionData.options)
            ? JSON.stringify(questionData.options.map(option => typeof option === 'string' ? option : option.label))
            : null
        ),
        settingsJson: questionData.settingsJson ?? null,
        validationRulesJson: questionData.validationRulesJson ?? null,
        styleJson: questionData.styleJson ?? null,
        minValue: questionData.minValue,
        maxValue: questionData.maxValue,
        defaultValue: questionData.defaultValue
      };
      
      console.log('📦 Sending update payload:', payload);
      
      const response = await questionsAPI.update(id, payload);
      
      console.log('✅ Survey question updated:', response.data);
      
      // Handle new API response format
      if (response.data && response.data.success) {
        return response.data.data;
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Error updating survey question:', error);
      const errorMessage = error.response?.data?.title || 
                          error.response?.data?.message || 
                          (error.response?.data?.errors ? JSON.stringify(error.response.data.errors) : 'Unknown error') ||
                          'Failed to update survey question';
      throw new Error(errorMessage);
    }
  },

  async deleteSurveyQuestion(id) {
    try {
      const response = await questionsAPI.delete(id);
      
      // Handle new API response format
      if (response.data && response.data.success) {
        return response.data.data;
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting survey question:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete survey question');
    }
  },

  async reorderQuestions(questionOrders) {
    try {
      const response = await questionsAPI.reorder(questionOrders);
      
      // Handle new API response format
      if (response.data && response.data.success) {
        return response.data.data;
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Error reordering questions:', error);
      throw new Error(error.response?.data?.message || 'Failed to reorder questions');
    }
  },

async getQuestionsBySurvey(surveyId) {
  try {
    console.log('🔄 [QUESTION SERVICE] Fetching questions for survey:', surveyId);
    
    const response = await questionsAPI.getBySurvey(surveyId);
    
    console.log('✅ [QUESTION SERVICE] Survey questions response:', {
      status: response.status,
      data: response.data,
      responseStructure: 'Full response for debugging'
    });
    
    // Return the data directly - let the component handle the structure
    return response.data;
  } catch (error) {
    console.error('❌ [QUESTION SERVICE] Error fetching survey questions:', error);
    
    console.error('🔍 [QUESTION SERVICE] Detailed error info:', {
      status: error.response?.status,
      data: error.response?.data,
      surveyId: surveyId
    });
    
    throw new Error(error.response?.data?.message || 'Failed to load survey questions');
  }
}
};